import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation } from '@react-navigation/native';
import { useState } from 'react';
import {
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAuth } from '../context/AuthContext';
import { useMissions } from '../context/MissionsContext';
import { useT } from '../i18n/useT';

export default function OrderPipelineScreen() {
  const t = useT();
  const navigation = useNavigation<any>();
  const { user } = useAuth();
  // Panier RÉEL (DB-driven via MissionsContext), pas de données mockées.
  const { cartItems } = useMissions();

  const [currentStep, setCurrentStep] = useState(1);
  const [orderData, setOrderData] = useState({
    billingInfo: {
      fullName: [user?.prenom, user?.nom].filter(Boolean).join(' ').trim(),
      email: user?.email ?? '',
      address: '',
      city: '',
      postalCode: '',
      country: 'France'
    },
    paymentMethod: {
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      nameOnCard: ''
    }
  });

  const steps = [
    { id: 1, name: 'Billing Info', icon: 'person' },
    { id: 2, name: 'Payment', icon: 'card' },
    { id: 3, name: 'Confirmation', icon: 'checkmark' }
  ];

  const subtotal = cartItems.reduce((sum, item) => sum + item.price, 0);
  const tax = subtotal * 0.08; // 8% tax
  const total = subtotal + tax;

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      // Navigate back to main screen after completion
      navigation.navigate('Main' as never);
    }
  };

  const handleBack = () => {
    navigation.goBack();
  };

  const handleInputChange = (section: 'billingInfo' | 'paymentMethod', field: string, value: string) => {
    setOrderData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const renderStep1 = () => (
    <View style={styles.stepContent}>
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>{t('checkout.fullNameLabel')}</Text>
        <TextInput
          style={styles.textInput}
          value={orderData.billingInfo.fullName}
          onChangeText={(value) => handleInputChange('billingInfo', 'fullName', value)}
          placeholder={t('checkout.fullName')}
        />
      </View>

              <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>{t('checkout.emailLabel')}</Text>
          <View style={styles.emailContainer}>
            <TextInput
              style={styles.textInput}
              value={orderData.billingInfo.email}
              onChangeText={(value) => handleInputChange('billingInfo', 'email', value)}
              placeholder={t('checkout.email')}
            />
          </View>
        </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>{t('checkout.address')}</Text>
        <TextInput
          style={styles.textInput}
          value={orderData.billingInfo.address}
          onChangeText={(value) => handleInputChange('billingInfo', 'address', value)}
          placeholder={t('checkout.sampleStreet')}
        />
      </View>

      <View style={styles.row}>
        <View style={[styles.inputGroup, styles.halfWidth]}>
          <Text style={styles.inputLabel}>{t('checkout.city')}</Text>
          <TextInput
            style={styles.textInput}
            value={orderData.billingInfo.city}
            onChangeText={(value) => handleInputChange('billingInfo', 'city', value)}
            placeholder={t('checkout.sampleCity')}
          />
        </View>
        <View style={[styles.inputGroup, styles.halfWidth]}>
          <Text style={styles.inputLabel}>{t('checkout.postalCode')}</Text>
          <TextInput
            style={styles.textInput}
            value={orderData.billingInfo.postalCode}
            onChangeText={(value) => handleInputChange('billingInfo', 'postalCode', value)}
            placeholder="10001"
          />
        </View>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>{t('checkout.country')}</Text>
        <TextInput
          style={styles.textInput}
          value={orderData.billingInfo.country}
          onChangeText={(value) => handleInputChange('billingInfo', 'country', value)}
          placeholder={t('checkout.sampleCountry')}
        />
      </View>
    </View>
  );

  const renderStep2 = () => (
    <View style={styles.stepContent}>
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>{t('checkout.cardNumber')}</Text>
        <View style={styles.cardInputContainer}>
          <TextInput
            style={styles.textInput}
            value={orderData.paymentMethod.cardNumber}
            onChangeText={(value) => handleInputChange('paymentMethod', 'cardNumber', value)}
            placeholder="1234 5678 9012 3456"
            maxLength={19}
            keyboardType="numeric"
          />
          <Ionicons name="card" size={16} color="#6B7280" style={styles.inputIcon} />
        </View>
      </View>

      <View style={styles.row}>
        <View style={[styles.inputGroup, styles.halfWidth]}>
          <Text style={styles.inputLabel}>{t('checkout.expiry')}</Text>
          <TextInput
            style={styles.textInput}
            value={orderData.paymentMethod.expiryDate}
            onChangeText={(value) => handleInputChange('paymentMethod', 'expiryDate', value)}
            placeholder="MM/YY"
            maxLength={5}
          />
        </View>
        <View style={[styles.inputGroup, styles.halfWidth]}>
          <Text style={styles.inputLabel}>CVV</Text>
          <View style={styles.cvvInputContainer}>
            <TextInput
              style={styles.textInput}
              value={orderData.paymentMethod.cvv}
              onChangeText={(value) => handleInputChange('paymentMethod', 'cvv', value)}
              placeholder="123"
              maxLength={4}
              keyboardType="numeric"
              secureTextEntry
            />
            <Ionicons name="lock-closed" size={16} color="#6B7280" style={styles.inputIcon} />
          </View>
        </View>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>{t('checkout.nameOnCard')}</Text>
        <TextInput
          style={styles.textInput}
          value={orderData.paymentMethod.nameOnCard}
          onChangeText={(value) => handleInputChange('paymentMethod', 'nameOnCard', value)}
          placeholder={t('checkout.sampleName')}
        />
      </View>

      <View style={styles.securityCard}>
        <View style={styles.securityContent}>
          <Ionicons name="lock-closed" size={16} color="#10B981" />
          <View style={styles.securityText}>
            <Text style={styles.securityTitle}>{t('checkout.securePayment')}</Text>
            <Text style={styles.securityDescription}>
              {t('checkout.secure')}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );

  const renderStep3 = () => (
    <View style={styles.stepContent}>
      <View style={styles.confirmationHeader}>
        <View style={styles.confirmationIcon}>
          <Ionicons name="checkmark" size={32} color="#10B981" />
        </View>
        <Text style={styles.confirmationTitle}>{t('checkout.orderConfirmedTitle')}</Text>
        <Text style={styles.confirmationSubtitle}>
          {t('checkout.thanks')}
        </Text>
      </View>

      <View style={styles.orderSummaryCard}>
        <Text style={styles.orderSummaryTitle}>{t('checkout.orderSummary')}</Text>
        <View style={styles.summaryDetails}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t('checkout.orderNumber')}</Text>
            <Text style={styles.summaryValue}>#MIS-2024-001</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t('checkout.emailLabel')}</Text>
            <Text style={styles.summaryValue}>{orderData.billingInfo.email}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t('checkout.total')}</Text>
            <Text style={styles.summaryValue}>${total.toFixed(2)}</Text>
          </View>
        </View>
      </View>

      <View style={styles.confirmationDetails}>
        <Text style={styles.confirmationDetail}>✅ Order confirmation sent to {orderData.billingInfo.email}</Text>
        <Text style={styles.confirmationDetail}>{t('checkout.missionsAdded')}</Text>
        <Text style={styles.confirmationDetail}>{t('checkout.accessImmediate')}</Text>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <Ionicons name="arrow-back" size={20} color="#374151" />
          </TouchableOpacity>
          <View style={styles.headerText}>
            <Text style={styles.headerTitle}>{t('checkout.title')}</Text>
            <Text style={styles.headerSubtitle}>
              Step {currentStep} of {steps.length}
            </Text>
          </View>
        </View>
      </View>

      {/* Progress Steps */}
      <View style={styles.progressContainer}>
        <View style={styles.progressSteps}>
          {steps.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            
            return (
              <View key={step.id} style={styles.stepContainer}>
                <View style={[
                  styles.stepIcon,
                  isActive && styles.activeStepIcon,
                  isCompleted && styles.completedStepIcon
                ]}>
                  {isCompleted ? (
                    <Ionicons name="checkmark" size={16} color="#FFFFFF" />
                  ) : (
                    <Ionicons name={step.icon as any} size={16} color={isActive ? "#FFFFFF" : "#6B7280"} />
                  )}
                </View>
                <Text style={[
                  styles.stepName,
                  isActive && styles.activeStepName,
                  isCompleted && styles.completedStepName
                ]}>
                  {step.name}
                </Text>
                {index < steps.length - 1 && (
                  <View style={[
                    styles.stepConnector,
                    isCompleted && styles.completedStepConnector
                  ]} />
                )}
              </View>
            );
          })}
        </View>
      </View>

      {/* Content */}
      <ScrollView style={styles.content}>
        <View style={styles.contentContainer}>
          {/* Current Step Content */}
          <View style={styles.stepWrapper}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </View>

          {/* Order Summary (visible on steps 1 and 2) */}
          {currentStep < 3 && (
            <View style={styles.orderSummaryCard}>
              <Text style={styles.orderSummaryTitle}>{t('checkout.orderSummary')}</Text>
              <View style={styles.orderItems}>
                {cartItems.map((item) => (
                  <View key={item.id} style={styles.orderItem}>
                    <View style={styles.orderItemContent}>
                      <Text style={styles.orderItemTitle} numberOfLines={2}>{item.title}</Text>
                      {item.isOnSale && (
                        <View style={styles.saleInfo}>
                          <Text style={styles.originalPrice}>${item.originalPrice}</Text>
                          <View style={styles.saleBadge}>
                            <Text style={styles.saleBadgeText}>{t('card.sale')}</Text>
                          </View>
                        </View>
                      )}
                    </View>
                    <Text style={styles.orderItemPrice}>${item.price}</Text>
                  </View>
                ))}
              </View>
              
              <View style={styles.orderTotal}>
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>{t('checkout.subtotal')}</Text>
                  <Text style={styles.totalValue}>${subtotal.toFixed(2)}</Text>
                </View>
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>{t('checkout.tax')}</Text>
                  <Text style={styles.totalValue}>${tax.toFixed(2)}</Text>
                </View>
                <View style={[styles.totalRow, styles.finalTotal]}>
                  <Text style={styles.finalTotalLabel}>{t('checkout.total')}</Text>
                  <Text style={styles.finalTotalValue}>${total.toFixed(2)}</Text>
                </View>
              </View>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Footer */}
      <View style={styles.footer}>
        <TouchableOpacity 
          style={[
            styles.continueButton,
            currentStep === 1 && !orderData.billingInfo.fullName && styles.disabledButton
          ]}
          onPress={handleNext}
          disabled={currentStep === 1 && !orderData.billingInfo.fullName}
        >
          <Text style={styles.continueButtonText}>
            {t(currentStep === 3 ? 'checkout.continueToMissions' : 'checkout.continue')}
          </Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  header: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    padding: 8,
    marginRight: 12,
  },
  headerText: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 2,
  },
  progressContainer: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  progressSteps: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stepContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  stepIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  activeStepIcon: {
    borderColor: '#3B82F6',
    backgroundColor: '#3B82F6',
  },
  completedStepIcon: {
    borderColor: '#10B981',
    backgroundColor: '#10B981',
  },
  stepName: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6B7280',
    marginLeft: 8,
  },
  activeStepName: {
    color: '#3B82F6',
  },
  completedStepName: {
    color: '#10B981',
  },
  stepConnector: {
    width: 16,
    height: 1,
    backgroundColor: '#D1D5DB',
    marginHorizontal: 8,
  },
  completedStepConnector: {
    backgroundColor: '#10B981',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    gap: 24,
  },
  stepWrapper: {
    gap: 16,
  },
  stepContent: {
    gap: 16,
  },
  inputGroup: {
    gap: 8,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#FFFFFF',
  },
  disabledInput: {
    backgroundColor: '#F3F4F6',
    color: '#6B7280',
  },
  emailContainer: {
    position: 'relative',
  },
  verifiedBadge: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: [{ translateY: -8 }],
    backgroundColor: '#D1FAE5',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  verifiedText: {
    fontSize: 12,
    color: '#10B981',
    fontWeight: '500',
  },
  verifiedNote: {
    fontSize: 12,
    color: '#6B7280',
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  halfWidth: {
    flex: 1,
  },
  cardInputContainer: {
    position: 'relative',
  },
  cvvInputContainer: {
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: [{ translateY: -8 }],
  },
  securityCard: {
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    padding: 16,
  },
  securityContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  securityText: {
    flex: 1,
  },
  securityTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#10B981',
    marginBottom: 4,
  },
  securityDescription: {
    fontSize: 12,
    color: '#6B7280',
  },
  confirmationHeader: {
    alignItems: 'center',
    gap: 16,
  },
  confirmationIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#D1FAE5',
    justifyContent: 'center',
    alignItems: 'center',
  },
  confirmationTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#111827',
  },
  confirmationSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
  },
  orderSummaryCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  orderSummaryTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 12,
  },
  orderItems: {
    gap: 12,
    marginBottom: 16,
  },
  orderItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  orderItemContent: {
    flex: 1,
    marginRight: 12,
  },
  orderItemTitle: {
    fontSize: 14,
    color: '#111827',
    marginBottom: 4,
  },
  saleInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  originalPrice: {
    fontSize: 12,
    color: '#6B7280',
    textDecorationLine: 'line-through',
  },
  saleBadge: {
    backgroundColor: '#FEE2E2',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  saleBadgeText: {
    fontSize: 10,
    color: '#EF4444',
    fontWeight: '500',
  },
  orderItemPrice: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  orderTotal: {
    gap: 8,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalLabel: {
    fontSize: 14,
    color: '#6B7280',
  },
  totalValue: {
    fontSize: 14,
    color: '#111827',
  },
  finalTotal: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 8,
  },
  finalTotalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  finalTotalValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  summaryDetails: {
    gap: 8,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: 14,
    color: '#6B7280',
  },
  summaryValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  confirmationDetails: {
    gap: 8,
  },
  confirmationDetail: {
    fontSize: 14,
    color: '#6B7280',
  },
  footer: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  continueButton: {
    backgroundColor: '#3B82F6',
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  disabledButton: {
    backgroundColor: '#9CA3AF',
  },
  continueButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
}); 